import { Component, type ErrorInfo, type ReactNode } from "react";

interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  error: Error | null;
}

// Class component because React error boundaries have no hook/functional equivalent.
// Deliberately has no dependency on stores/router/design-system — it must still render
// a usable fallback if the crash originated in one of those.
export class RootErrorBoundary extends Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
  state: RootErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error in portal-web:", error, errorInfo.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            display: "flex",
            minHeight: "100vh",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            textAlign: "center",
            fontFamily: "system-ui, sans-serif",
          }}
        >
          {/* eslint-disable i18next/no-literal-string -- deliberately untranslated: this fallback
              must still render if i18n itself is what crashed, so it cannot depend on useTranslation/t() */}
          <div>
            <h1 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.5rem" }}>Something went wrong</h1>
            <p style={{ color: "#6b7280", marginBottom: "1rem" }}>
              Please refresh the page. If the problem continues, contact your healthcare provider.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "0.375rem",
                border: "1px solid #d1d5db",
                background: "white",
                cursor: "pointer",
              }}
            >
              Refresh
            </button>
          </div>
          {/* eslint-enable i18next/no-literal-string */}
        </div>
      );
    }

    return this.props.children;
  }
}
